import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { formatRelativeTime, getRoleDisplay } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Scroll,
  Users,
  Loader2,
  Link as LinkIcon,
  Copy,
  Gamepad2,
  Upload,
  Store,
} from 'lucide-react';

export default function CampaignsPage() {
  const { user, language } = useAuth();
  const { campaigns, fetchCampaigns, createCampaign, joinCampaign, loading } = useCampaign();
  const navigate = useNavigate();
  
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [gameSystems, setGameSystems] = useState([]);

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    universe: 'D&D 5e',
    style: '',
    house_rules: '',
    system_id: ''
  });

  const [joinCode, setJoinCode] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchCampaigns().catch(console.error);
  }, [fetchCampaigns]);

  useEffect(() => {
    axios.get('/api/game-systems').then(r => setGameSystems(r.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCampaign.name.trim()) {
      toast.error(t('error_required', language));
      return;
    }
    
    setCreating(true);
    try {
      const campaign = await createCampaign(newCampaign);
      toast.success(t('success_saved', language));
      setCreateOpen(false);
      setNewCampaign({ name: '', description: '', universe: 'D&D 5e', style: '', house_rules: '', system_id: '' });
      navigate(`/campaigns/${campaign.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) {
      toast.error(t('error_required', language));
      return;
    }
    
    setJoining(true);
    try {
      const result = await joinCampaign(joinCode, characterName);
      toast.success(language === 'fr' ? 'Vous avez rejoint la campagne !' : 'You joined the campaign!');
      setJoinOpen(false);
      setJoinCode('');
      setCharacterName('');
      navigate(`/campaigns/${result.campaign_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setJoining(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/campaigns/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(language === 'fr'
        ? `Campagne "${res.data.campaign_name}" importée`
        : `Campaign "${res.data.campaign_name}" imported`);
      await fetchCampaigns();
      navigate(`/campaigns/${res.data.campaign_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur d\'import' : 'Import failed'));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <MainLayout showSidebar={false}>
      <div className="space-y-8 animate-fade-in" data-testid="campaigns-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">{t('campaigns', language)}</h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Vos aventures épiques vous attendent'
                : 'Your epic adventures await'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Marketplace */}
            <Button variant="outline" onClick={() => navigate('/marketplace')}>
              <Store className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t('marketplace', language)}</span>
            </Button>

            {/* Join Campaign */}
            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="join-campaign-btn">
                  <LinkIcon className="mr-2 h-4 w-4" />
                  {t('campaign_join', language)}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleJoin}>
                  <DialogHeader>
                    <DialogTitle className="font-cinzel">{t('campaign_join', language)}</DialogTitle>
                    <DialogDescription>
                      {language === 'fr' 
                        ? 'Entrez le code d\'invitation pour rejoindre une campagne'
                        : 'Enter the invite code to join a campaign'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="join-code">{t('campaign_invite_code', language)}</Label>
                      <Input
                        id="join-code"
                        placeholder="abc123xyz"
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value)}
                        required
                        data-testid="join-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="character-name">
                        {language === 'fr' ? 'Nom du personnage' : 'Character name'} ({t('optional', language)})
                      </Label>
                      <Input
                        id="character-name"
                        placeholder={language === 'fr' ? 'Ex: Aragorn le Rôdeur' : 'Ex: Aragorn the Ranger'}
                        value={characterName}
                        onChange={(e) => setCharacterName(e.target.value)}
                        data-testid="character-name-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setJoinOpen(false)}>
                      {t('cancel', language)}
                    </Button>
                    <Button type="submit" disabled={joining} data-testid="join-submit">
                      {joining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {t('campaign_join', language)}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Import Campaign */}
            <Button variant="outline" disabled={importing} asChild={!importing}>
              {importing ? (
                <span><Loader2 className="mr-2 h-4 w-4 animate-spin" />{language === 'fr' ? 'Import...' : 'Importing...'}</span>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Importer' : 'Import'}
                  <input type="file" accept=".json" className="hidden" onChange={handleImport} />
                </label>
              )}
            </Button>

            {/* Create Campaign */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="fantasy-button" data-testid="create-campaign-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('campaign_create', language)}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <form onSubmit={handleCreate}>
                  <DialogHeader>
                    <DialogTitle className="font-cinzel">{t('campaign_create', language)}</DialogTitle>
                    <DialogDescription>
                      {language === 'fr' 
                        ? 'Créez une nouvelle campagne et invitez vos joueurs'
                        : 'Create a new campaign and invite your players'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label htmlFor="campaign-name">{t('campaign_name', language)} *</Label>
                      <Input
                        id="campaign-name"
                        placeholder={language === 'fr' ? 'La Malédiction de Strahd' : 'Curse of Strahd'}
                        value={newCampaign.name}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                        required
                        data-testid="campaign-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="campaign-desc">{t('campaign_description', language)}</Label>
                      <Textarea
                        id="campaign-desc"
                        placeholder={language === 'fr' 
                          ? 'Une aventure sombre dans les terres de Barovia...'
                          : 'A dark adventure in the lands of Barovia...'}
                        value={newCampaign.description}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        data-testid="campaign-desc-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="campaign-universe">{t('campaign_universe', language)}</Label>
                      <Input
                        id="campaign-universe"
                        placeholder="D&D 5e, Forgotten Realms, Eberron..."
                        value={newCampaign.universe}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, universe: e.target.value }))}
                        data-testid="campaign-universe-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="campaign-style">{t('campaign_style', language)}</Label>
                      <Input
                        id="campaign-style"
                        placeholder={language === 'fr' ? 'Horreur gothique, exploration, politique...' : 'Gothic horror, exploration, political...'}
                        value={newCampaign.style}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, style: e.target.value }))}
                        data-testid="campaign-style-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="campaign-rules">{t('campaign_house_rules', language)}</Label>
                      <Textarea
                        id="campaign-rules"
                        placeholder={language === 'fr'
                          ? 'Règles personnalisées pour cette campagne...'
                          : 'Custom rules for this campaign...'}
                        value={newCampaign.house_rules}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, house_rules: e.target.value }))}
                        rows={2}
                        data-testid="campaign-rules-input"
                      />
                    </div>
                    {gameSystems.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="campaign-system">
                          <Gamepad2 className="inline h-4 w-4 mr-1 mb-0.5" />
                          {t('campaign_system_optional', language)}
                        </Label>
                        <select
                          id="campaign-system"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={newCampaign.system_id}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, system_id: e.target.value }))}
                        >
                          <option value="">{t('campaign_system_none', language)}</option>
                          {gameSystems.map(s => (
                            <option key={s.id} value={s.id}>{s.name} {s.version ? `(${s.version})` : ''}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                      {t('cancel', language)}
                    </Button>
                    <Button type="submit" disabled={creating} className="fantasy-button" data-testid="create-submit">
                      {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {t('create', language)}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Campaigns Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="glass-card">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                    <div className="h-20 bg-muted rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-16 text-center">
              <Scroll className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
              <h3 className="font-cinzel text-xl font-bold mb-2">{t('dashboard_no_campaigns', language)}</h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {t('dashboard_create_first', language)}
              </p>
              <div className="flex items-center justify-center gap-4">
                <Button variant="outline" onClick={() => setJoinOpen(true)}>
                  <LinkIcon className="mr-2 h-4 w-4" />
                  {t('campaign_join', language)}
                </Button>
                <Button onClick={() => setCreateOpen(true)} className="fantasy-button">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('campaign_create', language)}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => {
              const userMember = campaign.members.find(m => m.user_id === user?.id);
              return (
                <Card 
                  key={campaign.id} 
                  className="glass-card card-interactive card-lift cursor-pointer"
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  data-testid={`campaign-card-${campaign.id}`}
                >
                  {campaign.banner_url && (
                    <div 
                      className="h-40 bg-cover bg-center rounded-t-lg"
                      style={{ backgroundImage: `url(${campaign.banner_url})` }}
                    />
                  )}
                  <CardHeader className={campaign.banner_url ? 'pt-4' : ''}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="font-cinzel truncate">{campaign.name}</CardTitle>
                        <CardDescription className="mt-1">
                          {campaign.universe || 'D&D 5e'}
                          {campaign.style && ` • ${campaign.style}`}
                        </CardDescription>
                      </div>
                      {userMember && (
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                          userMember.role === 'owner' ? 'bg-primary/10 text-primary' : 'bg-secondary text-secondary-foreground'
                        }`}>
                          {getRoleDisplay(userMember.role, language)}
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {campaign.description || (language === 'fr' ? 'Aucune description' : 'No description')}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{campaign.members.length} {language === 'fr' ? 'membres' : 'members'}</span>
                      </div>
                      <span>{formatRelativeTime(campaign.created_at, language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
